if (!customElements.get('personalisation-input')) {
  // Maps the Shopify "Colour" option value to the CoolConfetti colour slug.
  const COLOUR_MAP = {
    Pink: 'Pink',
    Black: 'Black',
    Gold: 'Gold',
    Silver: 'Silver',
    Orange: 'Orange',
    Turquoise: 'Turquoise',
    Red: 'Red',
    Purple: 'Purple',
    Green: 'Green',
    White: 'White',
    Blue: 'BabyBlue',
    'Baby Pink': 'BabyPink',
  };

  const WORD_PATTERN = /^[A-Za-z0-9?!&#]+$/;

  function selectedColourName() {
    const picker = document.querySelector('variant-selects');
    if (!picker) return null;

    // Swatch and pill options render as fieldsets with a legend naming the option.
    for (const fieldset of picker.querySelectorAll('fieldset')) {
      const legend = fieldset.querySelector('legend');
      if (legend && /colou?r/i.test(legend.textContent)) {
        const checked = fieldset.querySelector('input:checked');
        if (checked) return checked.value.trim();
      }
    }

    // Dropdown options render as a select named options[Colour].
    const select = picker.querySelector('select[name*="Colour" i], select[name*="Color" i]');
    if (select) return select.value.trim();

    return null;
  }

  // The product .js URL Dawn exposes (data-url on variant-selects / product-info).
  function productJsonUrl() {
    const vs = document.querySelector('variant-selects');
    const pi = document.querySelector('product-info');
    const url = (vs && vs.dataset.url) || (pi && pi.dataset.url) || window.location.pathname;
    return url.split('?')[0].replace(/\/$/, '') + '.js';
  }

  // From a parsed product JSON, build a Map of packName → price (pence) for the
  // currently selected colour. Pack/Colour positions come from product.options.
  // Returns null if it can't be resolved.
  function packPricesFromProduct(product, currentColour) {
    if (!product || !Array.isArray(product.variants) || !Array.isArray(product.options)) {
      return null;
    }

    const names = product.options.map((o) => (o && o.name ? o.name : o));
    const packIndex = names.findIndex((n) => /pack|size/i.test(String(n)));
    if (packIndex < 0) return null;
    const colourIndex = names.findIndex((n) => /colou?r/i.test(String(n)));

    const prices = new Map();
    for (const variant of product.variants) {
      if (!Array.isArray(variant.options)) continue;
      const packName = variant.options[packIndex];
      if (packName === undefined) continue;
      if (currentColour && colourIndex >= 0 && variant.options[colourIndex] !== currentColour) {
        continue;
      }
      const existing = prices.get(packName);
      if (existing === undefined || variant.price < existing) {
        prices.set(packName, variant.price);
      }
    }
    return prices;
  }

  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  }

  // How long to wait after the last keystroke before auto-fixing spaces.
  const SPACE_FIX_DELAY = 3000;

  // Red warning while spaces are present, before the auto-fix runs.
  const SPACE_FIX_PENDING_MESSAGE = "Spaces aren't allowed. We'll remove them and capitalise for you.";

  // Green confirmation shown once the auto-fix has collapsed the spaces.
  const SPACE_FIX_DONE_MESSAGE = "Spaces aren't allowed. We've removed them and capitalised for you.";

  // Collapse a phrase into space-free text, capitalising the first letter of
  // each word and keeping the rest exactly as typed. Trims and collapses runs.
  function joinAndCapitalise(value) {
    return value
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join('');
  }

  // Format a price in pence as pounds, dropping the decimals for round amounts:
  // 500 → "£5", 850 → "£8.50".
  function formatPoundsSmart(pence) {
    const pounds = pence / 100;
    return '£' + (pence % 100 === 0 ? String(pounds) : pounds.toFixed(2));
  }

  // Stop variant changes from scrolling the page up to the media gallery.
  // Dawn's media-gallery.setActiveMedia(id, true) (the variant-change call) queues
  // a window.scrollTo; we neutralise just that page scroll for the duration of the
  // prepend call. Done as a monkey-patch from this custom asset so the stock
  // media-gallery.js stays untouched and Dawn upgrades cleanly.
  function patchMediaGalleryScroll() {
    const MediaGallery = customElements.get('media-gallery');
    if (!MediaGallery || MediaGallery.prototype.__ukNoVariantScroll) return;
    MediaGallery.prototype.__ukNoVariantScroll = true;

    const original = MediaGallery.prototype.setActiveMedia;
    MediaGallery.prototype.setActiveMedia = function (mediaId, prepend) {
      // Thumbnail clicks (prepend falsy) keep Dawn's default behaviour.
      if (!prepend) {
        return original.apply(this, arguments);
      }

      const realScrollTo = window.scrollTo;
      let restored = false;
      const restore = function () {
        if (restored) return;
        restored = true;
        window.scrollTo = realScrollTo;
      };

      // The gallery's own internal slider uses element.scrollTo, not
      // window.scrollTo, so only the page scroll is suppressed here.
      window.scrollTo = function () {};
      try {
        return original.apply(this, arguments);
      } finally {
        // Dawn queues its scroll via setTimeout during the call above, so this
        // restore (queued after) runs once that no-op scroll has fired.
        setTimeout(restore, 0);
        setTimeout(restore, 1000);
      }
    };
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('media-gallery').then(patchMediaGalleryScroll).catch(function () {});
  }

  customElements.define(
    'personalisation-input',
    class PersonalisationInput extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        if (!this.input) return;

        this.validateOnSubmit = this.validateOnSubmit.bind(this);
        this.refreshPreview = this.refreshPreview.bind(this);
        this.refreshPieces = this.refreshPieces.bind(this);

        const scope = this.closest('.product-form__input');
        this.preview = scope ? scope.querySelector('[data-confetti-preview]') : null;
        this.previewImg = this.preview ? this.preview.querySelector('.confetti-preview__img') : null;
        this.errorEl = scope ? scope.querySelector('[data-personalisation-error]') : null;
        this.errorText = scope ? scope.querySelector('[data-personalisation-error-text]') : null;
        this.imageField = scope ? scope.querySelector('[data-preview-image-field]') : null;

        // Track the latest pieces request word to guard against stale responses.
        this._latestPiecesWord = null;
        this._piecesContainer = null;
        this._lastMaxLength = 25;
        this._piecesPollTimer = null;
        this._piecesDeadline = 0;
        this._product = null;
        this._productLoading = false;
        this._lastPieces = null;
        this._pickerObserver = null;
        this._sublabelTimer = null;
        this._spaceFixTimer = null;

        // Show validation feedback the moment an invalid character is entered.
        this.input.addEventListener('input', () => this.validateLive());

        if (this.preview && this.previewImg) {
          this.previewImg.addEventListener('load', () => this.setState('loaded'));
          this.previewImg.addEventListener('error', () => this.setState('error'));

          this.input.addEventListener('input', debounce(this.refreshPreview, 350));

          // Re-render on every colour change. Delegated on document so it keeps
          // working even after Dawn re-renders the variant picker on a change.
          this.onColourChange = (event) => {
            if (event.target.closest && event.target.closest('variant-selects')) {
              this.refreshPreview();
            }
          };
          document.addEventListener('change', this.onColourChange);

          this.refreshPreview();
        }

        // Set up piece-count display if the preview endpoint is present.
        if (document.querySelector('[data-confetti-preview]')) {
          this._ensurePiecesContainer();
          this._ensureProductData();
          this._observePicker();

          // Stale counts must vanish the instant the field changes; the matching
          // fetch stays debounced so we don't hit the endpoint on every keystroke.
          this._refreshPiecesDebounced = debounce(this.refreshPieces, 350);
          this.input.addEventListener('input', () => {
            this._hideUnmatchedPieces();
            this._refreshPiecesDebounced();
          });

          this.onVariantChange = (event) => {
            if (event.target.closest && event.target.closest('variant-selects')) {
              this._rerenderFromCache();
            }
          };
          document.addEventListener('change', this.onVariantChange);

          this.refreshPieces();
        }

        // The product form is rendered after this element in the DOM, so the
        // input may not be associated with it yet at connect time. Wire up now
        // if it is, otherwise once the document has finished parsing.
        if (this.input.form) {
          this.wire();
        } else {
          document.addEventListener('DOMContentLoaded', () => this.wire(), { once: true });
        }
      }

      disconnectedCallback() {
        clearTimeout(this._piecesPollTimer);
        clearTimeout(this._sublabelTimer);
        clearTimeout(this._spaceFixTimer);
        this.scope?.removeEventListener('submit', this.validateOnSubmit, true);
        if (this.onColourChange) {
          document.removeEventListener('change', this.onColourChange);
        }
        if (this.onVariantChange) {
          document.removeEventListener('change', this.onVariantChange);
        }
        if (this._pickerObserver) {
          this._pickerObserver.disconnect();
          this._pickerObserver = null;
        }
      }

      wire() {
        if (!this.input.form) return;

        // Dawn renders the product form with `novalidate` and adds to cart via
        // an AJAX submit handler, so the input's native `required`/`pattern`
        // validation never runs. Listen on an ancestor of the form during the
        // capturing phase so this runs before Dawn's submit handler and can
        // block the add-to-cart while the field is invalid.
        this.scope = this.input.form.closest('product-form') || this.input.form.parentElement;
        if (!this.scope) return;

        this.scope.addEventListener('submit', this.validateOnSubmit, true);
      }

      validateOnSubmit(event) {
        // Spaces present: fix them now, show the notice, and block this one
        // submit so the change is visible. The corrected value is valid, so the
        // next click proceeds.
        if (/\s/.test(this.input.value)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.applySpaceFix();
          this.input.focus();
          return;
        }

        const message = this.validationMessageFor(this.input.value, true);
        if (!message) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError(message);
        this.input.focus();
      }

      // Returns a clear reason the value is invalid, or null when it is acceptable.
      validationMessageFor(value, forSubmit) {
        if (value === '') {
          return forSubmit ? 'Please enter a name or phrase to personalise.' : null;
        }
        if (/\s/.test(value)) {
          return "Spaces aren't allowed. Try joining the words, e.g. JaneSmith.";
        }
        if (value.length > 25) {
          return 'Please use 25 characters or fewer.';
        }
        if (!WORD_PATTERN.test(value)) {
          return 'Use only letters, numbers and ? ! & # (no other symbols).';
        }
        return null;
      }

      validateLive() {
        // Spaces present: warn now (softened to set expectations) and arm a
        // fresh idle timer; once it fires we collapse and capitalise the value.
        if (/\s/.test(this.input.value)) {
          this.showError(SPACE_FIX_PENDING_MESSAGE);
          clearTimeout(this._spaceFixTimer);
          this._spaceFixTimer = setTimeout(() => this.applySpaceFix(), SPACE_FIX_DELAY);
          return;
        }

        // No spaces: cancel any pending fix and fall back to base validation.
        clearTimeout(this._spaceFixTimer);

        const message = this.validationMessageFor(this.input.value, false);
        if (message) {
          this.showError(message);
        } else {
          this.clearError();
        }
      }

      // Collapse the spaces out of the field, re-run the normal input pipeline,
      // and confirm what we did. Called from the idle timer and from submit.
      applySpaceFix() {
        clearTimeout(this._spaceFixTimer);

        const fixed = joinAndCapitalise(this.input.value);

        // An all-whitespace value collapses to empty — clear quietly, no notice.
        if (fixed === '') {
          this.input.value = '';
          this.clearError();
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }

        this.input.value = fixed;

        // Re-run the preview, piece counts and base validation off a single
        // input event. The corrected value has no spaces, so validateLive takes
        // the base-validation path and won't re-arm the timer.
        this.input.dispatchEvent(new Event('input', { bubbles: true }));

        // If removing spaces left a different problem (e.g. an illegal '@'),
        // that red error is already showing from the dispatched event above.
        // Otherwise confirm the fix with the friendly notice.
        if (!this.validationMessageFor(fixed, false)) {
          this.showNotice(SPACE_FIX_DONE_MESSAGE);
        }
      }

      showError(message) {
        this.classList.add('has-error');
        this.input.setAttribute('aria-invalid', 'true');
        if (this.errorText) {
          this.errorText.textContent = message;
        }
        if (this.errorEl) {
          this.errorEl.classList.remove('personalisation-error--notice');
          this.errorEl.hidden = false;
        }
      }

      // A friendly, non-error confirmation (green, no red ring). Persists until
      // the next edit clears it via validateLive -> clearError.
      showNotice(message) {
        this.classList.remove('has-error');
        this.input.setAttribute('aria-invalid', 'false');
        if (this.errorText) {
          this.errorText.textContent = message;
        }
        if (this.errorEl) {
          this.errorEl.classList.add('personalisation-error--notice');
          this.errorEl.hidden = false;
        }
      }

      clearError() {
        this.classList.remove('has-error');
        this.input.setAttribute('aria-invalid', 'false');
        if (this.errorEl) {
          this.errorEl.classList.remove('personalisation-error--notice');
          this.errorEl.hidden = true;
        }
      }

      refreshPreview() {
        const word = this.input.value.trim();
        const colourName = selectedColourName();
        const slug = colourName ? COLOUR_MAP[colourName] : null;

        if (!WORD_PATTERN.test(word) || word.length > 25 || !slug) {
          this.setState('empty');
          return;
        }

        const endpoint = this.preview.getAttribute('data-endpoint');
        const url = `${endpoint}/${encodeURIComponent(slug)}/${encodeURIComponent(word)}`;

        // Already showing this exact image.
        if (this.previewImg.getAttribute('src') === url) {
          this.setState('loaded');
          return;
        }

        this.setState('loading');
        this.previewImg.setAttribute('src', url);
      }

      // Drive the preview UI: 'empty' (invite to type), 'loading' (shimmer),
      // 'loaded' (image + caption), or 'error' (collapse the block).
      setState(state) {
        if (!this.preview) return;

        if (state === 'error') {
          this.preview.hidden = true;
          this.preview.classList.remove('is-empty', 'is-loading', 'is-loaded');
          this.setCartImage('');
          return;
        }

        this.preview.hidden = false;
        this.preview.classList.toggle('is-empty', state === 'empty');
        this.preview.classList.toggle('is-loading', state === 'loading');
        this.preview.classList.toggle('is-loaded', state === 'loaded');

        // Pass the loaded preview through to the cart line item (cleared otherwise).
        this.setCartImage(state === 'loaded' ? this.previewImg.getAttribute('src') : '');
      }

      // Keep the hidden line-item property in sync so the cart can show the preview.
      setCartImage(url) {
        if (!this.imageField) return;
        this.imageField.value = url || '';
        this.imageField.disabled = !url;
      }

      // Ensure the pieces container exists and is attached. The isConnected
      // check lets it self-heal if it's ever removed.
      _ensurePiecesContainer() {
        if (this._piecesContainer && this._piecesContainer.isConnected) return;
        this._piecesContainer = null;

        const existing = document.querySelector('[data-confetti-pieces]');
        if (existing && existing.isConnected) {
          this._piecesContainer = existing;
          return;
        }

        const container = document.createElement('div');
        container.className = 'confetti-pieces';
        container.setAttribute('data-confetti-pieces', '');
        container.setAttribute('aria-live', 'polite');
        container.setAttribute('aria-atomic', 'true');

        // Preferred anchor: a sibling immediately after the variant picker, so the
        // panel reads as part of the size flow. The <variant-selects> element itself
        // persists across Dawn's re-renders (only its children are replaced), so a
        // sibling placed here is stable. (Placing it INSIDE the picker would be wiped.)
        const picker = document.querySelector('variant-selects');
        if (picker && picker.parentNode) {
          picker.parentNode.insertBefore(container, picker.nextSibling);
          this._piecesContainer = container;
          return;
        }

        const scope = this.closest('.product-form__input');
        if (scope) {
          scope.appendChild(container);
          this._piecesContainer = container;
          return;
        }

        // Fallback: after the confetti-preview block.
        const previewBlock = document.querySelector('[data-confetti-preview]');
        if (previewBlock && previewBlock.parentNode) {
          previewBlock.parentNode.insertBefore(container, previewBlock.nextSibling);
          this._piecesContainer = container;
        }
      }

      // Fetch piece counts for the current word and render the upsell display.
      // Counts are generated asynchronously, so when the endpoint reports it isn't
      // ready yet we keep "Calculating…" and poll until counts arrive or we give
      // up after ~10 seconds.
      refreshPieces() {
        clearTimeout(this._piecesPollTimer);

        const word = this.input.value.trim();

        this._ensurePiecesContainer();
        if (!this._piecesContainer) return;

        // Nothing typed or invalid word — clear the panel.
        if (!word || !WORD_PATTERN.test(word) || word.length > 25) {
          this._piecesContainer.innerHTML = '';
          this._latestPiecesWord = null;
          this._lastPieces = null;
          this._applyPackSublabels();
          return;
        }

        // Already have counts for this exact phrase (e.g. the shopper retyped it)
        // — show them at once, with no refetch or "Calculating…" flash.
        if (this._lastPieces && this._lastPieces.word === word) {
          this._latestPiecesWord = word;
          this._renderCounts(this._lastPieces.counts, word, this._lastPieces.maxLength);
          return;
        }

        const previewEl = document.querySelector('[data-confetti-preview]');
        if (!previewEl) return;

        const endpoint = previewEl.getAttribute('data-endpoint');
        if (!endpoint) return;

        // Derive pieces endpoint: replace trailing path segment `uk-preview` with `uk-pieces`.
        const piecesBase = endpoint.replace(/uk-preview$/, 'uk-pieces');

        this._renderCalculating();

        const requestWord = word;
        this._latestPiecesWord = requestWord;
        this._piecesDeadline = Date.now() + 10000;
        this._fetchPieces(piecesBase, requestWord);
      }

      // Immediately remove any counts that no longer match the field value. Runs
      // on every keystroke (the matching fetch is debounced separately), so stale
      // per-pill counts never linger while fresh ones are being calculated.
      _hideUnmatchedPieces() {
        const word = this.input.value.trim();

        // Cached counts still describe the field exactly — make sure they're
        // shown (e.g. the shopper deleted back to a phrase we already have).
        if (this._lastPieces && this._lastPieces.word === word) {
          this._rerenderFromCache();
          return;
        }

        // Strip the now-stale per-pill counts. The exact-match guard in
        // _computePackData makes this remove the labels rather than re-add them.
        this._applyPackSublabels();

        this._ensurePiecesContainer();
        if (!this._piecesContainer) return;
        if (!word || !WORD_PATTERN.test(word) || word.length > 25) {
          this._piecesContainer.innerHTML = '';
        } else {
          this._renderCalculating();
        }
      }

      // Counts depend only on the typed phrase, never on pack/colour, so a variant
      // change re-renders from cache instead of refetching (avoids a "Calculating…"
      // flash and a race with Dawn's picker swap).
      _rerenderFromCache() {
        if (this._lastPieces) {
          this._renderCounts(this._lastPieces.counts, this._lastPieces.word, this._lastPieces.maxLength);
        } else {
          this._applyPackSublabels();
        }
      }

      // One fetch attempt. While the server reports the counts aren't ready yet,
      // re-schedules itself every 1.5s until counts arrive or the ~10s deadline.
      _fetchPieces(piecesBase, requestWord) {
        fetch(`${piecesBase}/${encodeURIComponent(requestWord)}`, { credentials: 'omit' })
          .then((res) => {
            if (!res.ok) throw new Error('non-2xx');
            return res.json();
          })
          .then((data) => {
            // Discard if the word changed while in flight.
            if (this._latestPiecesWord !== requestWord) return;

            const counts = data && Array.isArray(data.counts) ? data.counts : [];

            if (data && data.ready && counts.length) {
              const maxLength = typeof data.max_length === 'number' ? data.max_length : 25;
              this._lastMaxLength = maxLength;
              this._renderCounts(counts, requestWord, maxLength);
              return;
            }

            // Not ready yet — keep "Calculating…" and poll until the deadline.
            if (Date.now() < this._piecesDeadline) {
              this._renderCalculating();
              this._piecesPollTimer = setTimeout(() => {
                if (this._latestPiecesWord === requestWord) {
                  this._fetchPieces(piecesBase, requestWord);
                }
              }, 1500);
            } else {
              this._renderStatic();
            }
          })
          .catch(() => {
            if (this._latestPiecesWord !== requestWord) return;
            // 404/403/network won't resolve by polling — show the fallback.
            this._renderStatic();
          });
      }

      _renderCalculating() {
        this._ensurePiecesContainer();
        if (!this._piecesContainer) return;
        this._piecesContainer.innerHTML =
          '<p class="confetti-pieces__calculating">Calculating piece counts…</p>';
      }

      _renderStatic() {
        this._ensurePiecesContainer();
        if (!this._piecesContainer) return;
        this._piecesContainer.innerHTML =
          '<p class="confetti-pieces__static">Piece counts vary by phrase length.</p>';
      }

      // Load the product JSON once (for variant prices) and re-render when ready.
      _ensureProductData() {
        if (this._product || this._productLoading) return;
        this._productLoading = true;
        fetch(productJsonUrl(), { credentials: 'omit' })
          .then((r) => (r.ok ? r.json() : null))
          .then((p) => {
            this._productLoading = false;
            if (!p) return;
            this._product = p;
            // Re-render the last counts now that prices are available.
            if (this._lastPieces) {
              this._renderCounts(this._lastPieces.counts, this._lastPieces.word, this._lastPieces.maxLength);
            }
            // Re-apply pill sublabels so they pick up prices even if the panel
            // itself wasn't re-rendered above.
            this._applyPackSublabels();
          })
          .catch(() => {
            this._productLoading = false;
          });
      }

      // Per-pack piece counts for the phrase currently in the field, as a
      // name → count map. Counts are only surfaced while they describe that
      // exact phrase; any mismatch returns null so stale counts are hidden
      // until a fresh fetch lands.
      _computePackData() {
        if (!this._lastPieces) return null;
        if (this._lastPieces.word !== this.input.value.trim()) return null;
        const byName = new Map(this._lastPieces.counts.map((c) => [c.name, c.count]));
        return { byName: byName };
      }

      // Refresh the long-phrase nudge and the per-pack ticket sublabels. The
      // upsell now lives on the size tickets themselves (price plus a Best value
      // tag on the largest pack), so there's no separate upgrade panel here.
      _renderCounts(counts, word, maxLength) {
        this._ensurePiecesContainer();
        if (!this._piecesContainer) return;

        // Remember for a re-render once product prices load (and for instant
        // redisplay if the shopper retypes the same phrase).
        this._lastPieces = { counts: counts, word: word, maxLength: maxLength };
        this._ensureProductData();

        // A fetch can resolve after the shopper has typed further. Never paint
        // counts for a phrase that no longer matches the field; the pending
        // debounced refresh will fetch the current phrase.
        if (word !== this.input.value.trim()) {
          this._hideUnmatchedPieces();
          return;
        }

        const showNudge = word.length > Math.floor(maxLength * 0.5);
        this._piecesContainer.innerHTML = showNudge
          ? '<div class="confetti-pieces__panel">' +
            '<p class="confetti-pieces__nudge">Shorter phrases = more pieces</p>' +
            '</div>'
          : '';

        this._applyPackSublabels();
      }

      // Rebuild each Pack Size pill in place as a "ticket": size name, price
      // (from the product variants), piece count when a phrase is typed, and a
      // single Best value tag on the largest (priciest) pack. Idempotent: safe
      // to re-run on every Dawn re-render and on every counts refresh.
      _applyPackSublabels() {
        const picker = document.querySelector('variant-selects');
        if (!picker) return;

        // One pass over the picker's fieldsets: lay the colour swatches out as a
        // full-width grid, and find the pack fieldset to rebuild as tickets.
        let packFieldset = null;
        for (const fs of picker.querySelectorAll('fieldset')) {
          const legend = fs.querySelector('legend');
          if (!legend) continue;
          if (/colou?r/i.test(legend.textContent)) {
            fs.classList.add('confetti-colour-grid');
          } else if (/pack|size/i.test(legend.textContent)) {
            packFieldset = fs;
          }
        }
        if (!packFieldset) return;

        packFieldset.classList.add('confetti-pack-tickets');

        // Prices come from the product variants; start the load if it hasn't run.
        this._ensureProductData();
        const packPrices = this._product
          ? packPricesFromProduct(this._product, selectedColourName())
          : null;

        const countData = this._computePackData();
        const inputs = Array.from(packFieldset.querySelectorAll('input[type="radio"]'));

        // Best value = the largest pack: the highest-priced available option.
        // Only surfaced when there's more than one pack to choose between.
        let bestValueName = null;
        if (packPrices && packPrices.size >= 2) {
          let topPrice = -Infinity;
          for (const input of inputs) {
            if (input.disabled || input.classList.contains('disabled')) continue;
            const name = input.value.trim();
            const price = packPrices.has(name) ? packPrices.get(name) : null;
            if (price !== null && price > topPrice) {
              topPrice = price;
              bestValueName = name;
            }
          }
        }

        for (const input of inputs) {
          const label = input.id ? packFieldset.querySelector('label[for="' + input.id + '"]') : null;
          if (!label) continue;

          // Clear what we injected last time, plus Dawn's bare name text node:
          // the name is re-rendered below so it can be styled on its own.
          for (const extra of label.querySelectorAll('.js-pack-extra')) {
            extra.remove();
          }
          for (const node of Array.from(label.childNodes)) {
            if (node.nodeType === 3) {
              label.removeChild(node);
            }
          }

          const name = input.value.trim();
          const price = packPrices && packPrices.has(name) ? packPrices.get(name) : null;
          const count = countData ? countData.byName.get(name) : undefined;

          const nameEl = document.createElement('span');
          nameEl.className = 'confetti-pack-name js-pack-extra';
          nameEl.textContent = name;
          label.insertBefore(nameEl, label.firstChild);

          if (price !== null) {
            const priceEl = document.createElement('span');
            priceEl.className = 'confetti-pack-price js-pack-extra';
            priceEl.textContent = formatPoundsSmart(price);
            label.appendChild(priceEl);
          }

          if (count !== undefined) {
            if (price !== null) {
              const perf = document.createElement('span');
              perf.className = 'confetti-pack-perf js-pack-extra';
              perf.setAttribute('aria-hidden', 'true');
              label.appendChild(perf);
            }
            const piecesEl = document.createElement('span');
            piecesEl.className = 'confetti-pack-pieces js-pack-extra';
            const numberEl = document.createElement('span');
            numberEl.className = 'confetti-pack-pieces__n';
            numberEl.textContent = '≈' + count;
            const unitEl = document.createElement('span');
            unitEl.className = 'confetti-pack-pieces__u';
            unitEl.textContent = 'pieces';
            piecesEl.appendChild(numberEl);
            piecesEl.appendChild(unitEl);
            label.appendChild(piecesEl);
          }

          if (bestValueName && name === bestValueName) {
            const sticker = document.createElement('span');
            sticker.className = 'confetti-pack-sticker js-pack-extra';
            sticker.textContent = '🎉 Most for your money';
            label.appendChild(sticker);
          }
        }
      }

      // Dawn replaces the whole <variant-selects> node on each variant change, so
      // we observe its stable parent (where Dawn inserts the replacement) with
      // childList only. childList-only means our own deep pill-label edits don't
      // notify the observer, so there's no feedback loop and no need to disconnect
      // while mutating.
      _observePicker() {
        if (this._pickerObserver) return;
        const picker = document.querySelector('variant-selects');
        const root = picker ? picker.parentNode : null;
        if (!root || typeof MutationObserver === 'undefined') return;
        this._pickerObserver = new MutationObserver(() => {
          clearTimeout(this._sublabelTimer);
          this._sublabelTimer = setTimeout(() => this._applyPackSublabels(), 60);
        });
        this._pickerObserver.observe(root, { childList: true });
      }
    }
  );
}
